import Sentry from "@similarweb/sentry-logger";

import { swSettings } from "app/common/services/swSettings";
import type {
    IDashboardItem,
    IKeywordListItem,
    ILastViewedItem,
    AssetType,
} from "app/pages/digital-suite/home-page/components/CustomAssets/types";
import { DefaultFetchService } from "app/services/fetchService";
import { keywordGroupsQueryClient } from "app/services/keyword-groups";
import type { IAccountUsersResponse } from "app/sharing/SharingService";

const fetchService = DefaultFetchService.getInstance();

export const getDashboards = async (): Promise<IDashboardItem[]> => {
    try {
        const isSharingEnabled = swSettings.components.ShareDashboard.resources.CanShareADashboard;
        const response: any = await fetchService.get("/api/userdata/dashboards");
        const sharedDashboards: any = isSharingEnabled ? await fetchService.get("/api/userdata/dashboards/shared") : [];
        const sharedUsers: IAccountUsersResponse = isSharingEnabled
            ? await fetchService.get("/api/userdata/dashboards/share-users")
            : { users: [] };

        const phraseFilterData: IDashboardItem[] = response.data.map(
            ({ addedTime = "", id = "", title = "" } = {}) => ({
                addedTime,
                id,
                name: title,
            }),
        );

        const phraseSharedDashboards: IDashboardItem[] = sharedDashboards
            .map(({ addedTime = "", id = "", title = "", ownerId = 0 } = {}) => {
                const ownerIdUser = sharedUsers.users.find((user) => ownerId === user.id);
                if (ownerIdUser) {
                    return {
                        addedTime,
                        id,
                        name: title,
                        owner: ownerIdUser,
                    };
                }
            })
            .filter((d: IDashboardItem | undefined) => d);

        return [...phraseFilterData, ...phraseSharedDashboards];
    } catch (error) {
        // add log
        return [];
    }
};

export const getKeywordLists = async (): Promise<IKeywordListItem[]> => {
    try {
        const isSharingEnabled = swSettings.components.ShareDashboard.resources.CanShareADashboard;
        const { userGroups, sharedGroups } = await keywordGroupsQueryClient.getAllGroups();
        const sharedUsers: IAccountUsersResponse = isSharingEnabled
            ? await fetchService.get("/api/userdata/dashboards/share-users")
            : { users: [] };

        const phraseOwnerGroups: IKeywordListItem[] = userGroups.map((group) => ({
            addedTime: group.AddedTime || "",
            id: group.Id,
            name: group.Name,
            keywords: group.Keywords,
        }));

        const phraseSharedGroups: IKeywordListItem[] = sharedGroups
            .map((group) => {
                const ownerId = group.UserId || "";
                const ownerIdUser = sharedUsers.users.find((user) => Number(ownerId) === user.id);
                if (ownerIdUser) {
                    return {
                        addedTime: group.AddedTime || "",
                        id: group.Id,
                        name: group.Name,
                        keywords: group.Keywords,
                        owner: ownerIdUser,
                    };
                }
                return undefined;
            })
            .filter((kl: IKeywordListItem | undefined) => kl !== undefined) as IKeywordListItem[];

        return [...phraseOwnerGroups, ...phraseSharedGroups];
    } catch (error) {
        Sentry.errorTracker({
            message: "Error fetching keyword lists",
            e: error,
            level: "error",
        });
        return [];
    }
};

export const updateLastViewedAsset = (assetId: string, assetType: AssetType): void => {
    try {
        fetchService.post(`api/userdata/assets/${assetId}/last-viewed`, {
            assetType,
        });
    } catch (error) {}
};

export const getLastViewedAssets = async (): Promise<ILastViewedItem[]> => {
    try {
        const response: ILastViewedItem[] = await fetchService.get("api/userdata/assets/last-viewed");
        return response;
    } catch (error) {
        return [];
    }
};
